import React, { createContext, useContext, useEffect, useState } from 'react';
import { authService } from './authService';

const AuthContext = createContext();

const normalizeRole = role => {
  if (role === 'lecturer') return 'teacher';
  return role;
};

const normalizeUser = user => {
  if (!user) return null;
  return {
    ...user,
    id: user.id ?? user._id,
    _id: user._id ?? user.id,
    role: normalizeRole(user.role),
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const saved = sessionStorage.getItem('user');
      return saved ? normalizeUser(JSON.parse(saved)) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Clear any old localStorage tokens from previous versions
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    const token = sessionStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    authService
      .getCurrentUser()
      .then(currentUser => {
        const safeUser = normalizeUser(currentUser);
        setUser(safeUser);
        sessionStorage.setItem('user', JSON.stringify(safeUser));
      })
      .catch(() => {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.login(email, password);
      const safeUser = normalizeUser(response.user);
      setUser(safeUser);
      sessionStorage.setItem('token', response.token);
      sessionStorage.setItem('user', JSON.stringify(safeUser));
      return true;
    } catch (err) {
      setError(err.message || 'Invalid email or password.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setError(null);
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
